import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "textarea"]

  connect() {
    this.initializeEditor()
  }

  disconnect() {
    if (this.view) {
      this.view.destroy()
    }
  }

  async initializeEditor() {
    // Show loading indicator
    this.editorTarget.innerHTML = '<div class="text-gray-500">Loading editor...</div>'
    
    try {
      // Dynamically import CodeMirror modules
      const [{ EditorState }, { EditorView, lineNumbers }] = await Promise.all([
        import("@codemirror/state"),
        import("@codemirror/view")
      ])

      const value = this.textareaTarget.value || ""

      // Create minimal editor state with line numbers
      const state = EditorState.create({
        doc: value,
        extensions: [
          lineNumbers(),
          EditorView.lineWrapping,
          EditorView.theme({
            "&": {
              fontSize: "1rem",
              fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              border: "1px solid #dee2e6",
              borderRadius: "0.375rem"
            },
            ".cm-content": {
              padding: "0.375rem 0.75rem",
              minHeight: "20rem"
            },
            ".cm-gutters": {
              backgroundColor: "#f8f9fa",
              color: "#6c757d",
              border: "none",
              borderRight: "1px solid #dee2e6"
            },
            ".cm-lineNumbers": {
              minWidth: "3.5rem"
            },
            ".cm-lineNumbers .cm-gutterElement": {
              textAlign: "right",
              padding: "0 0.5rem 0 0.25rem"
            },
            "&.cm-focused": {
              outline: "none"
            },
            ".cm-line": {
              lineHeight: "1.5rem"
            }
          }),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              // Update hidden textarea value when editor changes
              this.textareaTarget.value = update.state.doc.toString()
              // Dispatch input event for form validation
              this.textareaTarget.dispatchEvent(new Event('input', { bubbles: true }))
            }
          })
        ]
      })

      // Clear loading indicator
      this.editorTarget.innerHTML = ''

      // Create editor view
      this.view = new EditorView({
        state,
        parent: this.editorTarget
      })

      // Hide the original textarea
      this.textareaTarget.style.display = 'none'
    } catch (error) {
      console.error('Failed to load CodeMirror:', error)
      // Show error and fall back to textarea
      this.editorTarget.innerHTML = '<div class="text-red-600">Failed to load editor</div>'
      this.textareaTarget.style.display = 'block'
    }
  }
}
